import React, { useState, useCallback, useEffect, useContext, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Form } from 'semantic-ui-react';
import { UserContext } from 'src/context';
import { Roles } from 'src/utils/roles';
import { GetInitialBalances, GetManualDocuments } from 'src/graphql/initialBalanceSidebar';
import { useCompanyMentionUsers } from 'src/hooks/useCompanyMentionUsers';
import { useUpdateBitacora, useCreateBitacoraComment, analyzeBitacoraFlow } from 'src/graphql/bitacora';
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import { banksFormat } from 'src/utils/bankList';
import { importAlertState } from 'src/redux/actions/alert';
import moment from 'moment';
import BitacoraCommentsSection from './BitacoraCommentsSection';
import BitacoraEditor from 'src/modules/bitacora/BitacoraEditor';
import BitacoraUploadZone from './BitacoraUploadZone';
import BitacoraChatPanel from './BitacoraChatPanel';
import { renderBitacoraCommentWithMentions, markdownToEditorHtml } from './renderMarkdown';
import { getBitacoraDefaultTitle } from 'src/utils/financeI18n';
import aiIcon from 'src/assets/icons/ai-icon.svg';
import './bitacora.scss';
import './BitacoraModal.scss';

const formatDateTime = (date) => {
  if (!date) return '-';
  const d = moment(date);
  return d.format('DD/MM/YYYY HH:mm');
};

const BitacoraDetailView = forwardRef(({
  bitacora,
  companyId,
  canCreate,
  onBack,
  onSend,
  onNotify,
  onHistory,
  onRefresh,
  embedded = false,
  canNotify = false,
}, ref) => {
  const { t } = useTranslation('finance');
  const dispatch = useDispatch();
  const userInfo = useContext(UserContext);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const currentUser = useSelector((state) => state.auth);
  const effectiveCompanyId = companyId || selectedCompany?.id;
  const roles = userInfo?.roles ?? currentUser?.roles ?? [];
  const canViewInitialComments =
    roles?.includes(Roles.ADMIN) ||
    roles?.includes(Roles.SUPER_COLABORADOR) ||
    roles?.includes(Roles.COLABORADOR);

  const initialBalances = GetInitialBalances(effectiveCompanyId);
  const manualDocuments = GetManualDocuments(effectiveCompanyId);
  const bankBalances =
    Array.isArray(bitacora?.bankBalances) && bitacora.bankBalances.length > 0
      ? bitacora.bankBalances
      : (initialBalances?.data?.allBankBalances || []);
  const manualBalances = manualDocuments?.data?.allInitialBalanceDocumentsByCompany || [];
  const currentUserId = currentUser?.uid;
  const mentionUsers = useCompanyMentionUsers(effectiveCompanyId, currentUserId);

  const activeBankBalances = (initialBalances?.data?.allBankBalances || []).filter((b) => b?.isActive !== false);
  const initialBalancesForDisplay =
    Array.isArray(bitacora?.bankBalances) && bitacora.bankBalances.length > 0
      ? bitacora.bankBalances.map((b) => ({
          name: b.bankName || b.label || t('bitacora.bank'),
          amount: Number(b.initialBalance ?? 0) || 0,
          currencyType: b.currencyType || 'CLP',
          label: b.label,
        }))
      : [
          ...activeBankBalances.map((b) => ({
            name: banksFormat[b.bankId] || b.bankName || b.name || b.bankId || t('bitacora.bank'),
            amount: Number(b.availableBalance ?? b.initialBalance ?? b.balance ?? 0) || 0,
            currencyType: b.currencyType || 'CLP',
            label: b.label,
          })),
          ...manualBalances.map((m) => ({
            name: m.name || t('bitacora.manualBalance'),
            amount: Number(m.amount ?? 0) || 0,
            currencyType: m.currencyType || 'CLP',
            label: null,
          })),
        ];

  const isDraft = bitacora?.status === 'draft';
  const isDelivered = bitacora?.status === 'delivered';
  const canComment = isDelivered;
  const isEditable = canCreate && isDraft;

  const [title, setTitle] = useState(bitacora?.title || getBitacoraDefaultTitle(bitacora?.flowDate));
  const [initialComments, setInitialComments] = useState(bitacora?.initialComments || '');
  const [emailDraft, setEmailDraft] = useState(bitacora?.emailDraft || '');
  const [imageWithProjections, setImageWithProjections] = useState(
    bitacora?.imageWithProjectionsUrl ? { url: bitacora.imageWithProjectionsUrl } : null,
  );
  const [imageWithoutProjections, setImageWithoutProjections] = useState(
    bitacora?.imageWithoutProjectionsUrl ? { url: bitacora.imageWithoutProjectionsUrl } : null,
  );
  const [draftAiLoading, setDraftAiLoading] = useState(false);
  const [draftAiError, setDraftAiError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [saving, setSaving] = useState(false);
  const commentsSectionRef = React.useRef(null);

  const updateBitacora = useUpdateBitacora();
  const createComment = useCreateBitacoraComment();

  useEffect(() => {
    if (bitacora) {
      setTitle(bitacora.title || getBitacoraDefaultTitle(bitacora.flowDate));
      setInitialComments(bitacora.initialComments || '');
      setEmailDraft(bitacora.emailDraft || '');
      setImageWithProjections(bitacora.imageWithProjectionsUrl ? { url: bitacora.imageWithProjectionsUrl } : null);
      setImageWithoutProjections(bitacora.imageWithoutProjectionsUrl ? { url: bitacora.imageWithoutProjectionsUrl } : null);
    }
  }, [bitacora?.id]);

  const flowDateFormatted = bitacora?.flowDate
    ? (() => {
        try {
          const d = new Date(bitacora.flowDate);
          return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
          return moment(bitacora.flowDate).format('DD/MM/YYYY');
        }
      })()
    : '-';

  const commentsRaw = initialComments ?? bitacora?.initialComments ?? bitacora?.comentarios ?? '';
  const hasInitialComments = commentsRaw != null && String(commentsRaw).trim().replace(/<[^>]+>/g, '').trim() !== '';
  const latestEmailComment = (bitacora?.comments || [])
    .slice()
    .reverse()
    .find((c) => c?.source === 'email');
  const latestSentClientComment = latestEmailComment?.comment || '';
  const storedClientComment = bitacora?.emailDraft || '';
  const clientCommentRaw = isEditable
    ? emailDraft
    : (isDelivered ? (latestSentClientComment || storedClientComment) : (storedClientComment || latestSentClientComment));
  const hasClientComment = clientCommentRaw != null && String(clientCommentRaw).trim().replace(/<[^>]+>/g, '').trim() !== '';

  const hasBothImagesForAi =
    (imageWithProjections?.url || bitacora?.imageWithProjectionsUrl) &&
    (imageWithoutProjections?.url || bitacora?.imageWithoutProjectionsUrl);
  const canGenerateDraftWithAi = Boolean(hasBothImagesForAi);
  const hasDraftContent = Boolean((initialComments || '').replace(/<[^>]*>/g, '').trim().length > 20);
  const canOpenChat = isEditable && bitacora?.id && hasBothImagesForAi;

  const handleSave = useCallback(async () => {
    if (!bitacora?.id || !isEditable) return;
    setSaving(true);
    try {
      const input = {
        title: title?.trim() || getBitacoraDefaultTitle(bitacora.flowDate),
        initialComments: initialComments ?? '',
        emailDraft: emailDraft ?? '',
        imageWithProjectionsUrl: imageWithProjections?.url ?? bitacora?.imageWithProjectionsUrl ?? null,
        imageWithoutProjectionsUrl: imageWithoutProjections?.url ?? bitacora?.imageWithoutProjectionsUrl ?? null,
      };
      const { error } = await updateBitacora.executeMutation(bitacora.id, input);
      if (error) throw error;
      dispatch(importAlertState({ content: t('bitacora.draftSaved'), state: 'success', duration: 3 }));
      onRefresh?.();
    } catch (e) {
      dispatch(importAlertState({ content: e?.message || 'Error al guardar', state: 'error', duration: 5 }));
    } finally {
      setSaving(false);
    }
  }, [bitacora, title, initialComments, emailDraft, imageWithProjections, imageWithoutProjections, updateBitacora, dispatch, onRefresh, isEditable]);

  const handleDebouncedCommentsUpdate = useCallback(
    async (contenidoHtml) => {
      if (!bitacora?.id || !isEditable) return;
      try {
        await updateBitacora.executeMutation(bitacora.id, { initialComments: contenidoHtml });
      } catch {
        /* autosave silencioso */
      }
    },
    [bitacora?.id, isEditable, updateBitacora],
  );

  const handleDebouncedEmailDraftUpdate = useCallback(
    async (contenidoHtml) => {
      if (!bitacora?.id || !isEditable) return;
      try {
        await updateBitacora.executeMutation(bitacora.id, { emailDraft: contenidoHtml ?? '' });
      } catch {
        /* autosave silencioso */
      }
    },
    [bitacora?.id, isEditable, updateBitacora],
  );

  const handleImageChange = useCallback((type, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target.result;
      if (type === 'with') setImageWithProjections({ file, url });
      else setImageWithoutProjections({ file, url });
    };
    reader.readAsDataURL(file);
  }, []);

  const handleGenerateDraftWithAi = useCallback(async () => {
    if (draftAiLoading || !canGenerateDraftWithAi) return;
    const urlWith = imageWithProjections?.url || bitacora?.imageWithProjectionsUrl;
    const urlWithout = imageWithoutProjections?.url || bitacora?.imageWithoutProjectionsUrl;
    if (!urlWith || !urlWithout) return;
    setDraftAiLoading(true);
    setDraftAiError('');
    try {
      const balancesMap = {};
      (activeBankBalances || []).forEach((b) => {
        const name = banksFormat[b.bankId] || b.bankName || b.name || b.bankId || 'Banco';
        const balance = Number(b.availableBalance ?? b.initialBalance ?? b.balance ?? 0);
        balancesMap[name] = Number.isFinite(balance) ? balance : 0;
      });
      (manualBalances || []).forEach((m) => {
        const name = m.name || 'Saldo manual';
        balancesMap[name] = Number(m.amount ?? 0) || 0;
      });
      (bitacora?.bankBalances || []).forEach((b) => {
        const name = b.bankName || b.name || 'Banco';
        if (!(name in balancesMap)) balancesMap[name] = Number(b.initialBalance ?? 0) || 0;
      });
      if (Object.keys(balancesMap).length === 0) balancesMap['Saldo inicial'] = 0;

      const result = await analyzeBitacoraFlow({
        imageWithProjectionsUrl: urlWith,
        imageWithoutProjectionsUrl: urlWithout,
        companyName: selectedCompany?.name || selectedCompany?.alias || '',
        flowDate: bitacora?.flowDate ? moment(bitacora.flowDate).locale('es').format('D [de] MMMM [de] YYYY') : '',
        bankBalances: balancesMap,
      });
      const text = result?.analysis || '';
      if (text) setInitialComments(markdownToEditorHtml(text));
      if (result?.emailDraft) setEmailDraft(markdownToEditorHtml(result.emailDraft) || '<p></p>');
    } catch (err) {
      setDraftAiError(err?.message || 'No se pudo generar el borrador con IA.');
    } finally {
      setDraftAiLoading(false);
    }
  }, [draftAiLoading, canGenerateDraftWithAi, imageWithProjections, imageWithoutProjections, bitacora, activeBankBalances, manualBalances, selectedCompany, dispatch]);

  const handleBack = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    const t = setTimeout(() => onBack(), 280);
    return () => clearTimeout(t);
  }, [onBack, isExiting]);

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }), [handleSave]);

  return (
    <div className={`bitacora-detail-wrapper ${embedded ? 'bitacora-detail-wrapper--embedded' : ''} ${isExiting ? 'bitacora-detail-wrapper--exiting' : ''}`}>
      {!embedded && (
        <div className="bitacora-detail-back-wrap">
          <span
            className="bitacora-detail-back"
            onClick={handleBack}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleBack()}
          >
            ← Volver al listado
          </span>
        </div>
      )}
      <div className="bitacora-detail-container">
        <div className="bitacora-detail">
          {!embedded && (
            <div className="bitacora-detail-header">
              <div>
                {isEditable ? (
                  <Form.Input
                    value={title}
                    onChange={(_, { value }) => setTitle(value)}
                    onBlur={handleSave}
                    placeholder={getBitacoraDefaultTitle(bitacora?.flowDate)}
                    className="bitacora-detail-title-input"
                  />
                ) : (
                  <h3 className="bitacora-detail-title">{bitacora?.title || t('bitacora.noTitle')}</h3>
                )}
                <div className="bitacora-detail-meta">
                  <span className="bitacora-detail-date">{flowDateFormatted}</span>
                  <span className={`bitacora-detail-badge bitacora-detail-badge--${isDraft ? 'draft' : 'delivered'}`}>
                    {isDraft ? t('bitacora.draft') : t('bitacora.delivered')}
                  </span>
                </div>
              </div>
            </div>
          )}

      <div className="bitacora-detail-section bitacora-detail-section--images">
        <div className="bitacora-detail-section-title">{t('bitacora.images')}</div>
        <div className="bitacora-detail-images">
          <div className="bitacora-detail-image-col bitacora-detail-image-col--left">
            {isEditable ? (
              <BitacoraUploadZone
                label={t('bitacora.cashflowWithProjections')}
                value={imageWithProjections}
                onChange={(file) => handleImageChange('with', file)}
                onClear={() => setImageWithProjections(null)}
                disabled={false}
                accept="image/*"
              />
            ) : bitacora?.imageWithProjectionsUrl ? (
              <div className="bitacora-detail-image-block bitacora-detail-image-block--blue">
                <div className="bitacora-detail-image-label">{t('bitacora.cashflowWithProjections')}</div>
                <div className="bitacora-detail-image-wrap">
                  <img
                    src={bitacora.imageWithProjectionsUrl}
                    alt={t('bitacora.cashflowWithProjections')}
                    className="bitacora-detail-image-preview"
                    onClick={() => window.open(bitacora.imageWithProjectionsUrl, '_blank')}
                  />
                </div>
              </div>
            ) : (
              <div className="bitacora-detail-image-empty">{t('bitacora.noImageWithProjections')}</div>
            )}
          </div>
          <div className="bitacora-detail-image-col bitacora-detail-image-col--right">
            {isEditable ? (
              <BitacoraUploadZone
                label={t('bitacora.cashflowWithoutProjections')}
                value={imageWithoutProjections}
                onChange={(file) => handleImageChange('without', file)}
                onClear={() => setImageWithoutProjections(null)}
                disabled={false}
                accept="image/*"
              />
            ) : bitacora?.imageWithoutProjectionsUrl ? (
              <div className="bitacora-detail-image-block bitacora-detail-image-block--green">
                <div className="bitacora-detail-image-label">{t('bitacora.cashflowWithoutProjections')}</div>
                <div className="bitacora-detail-image-wrap">
                  <img
                    src={bitacora.imageWithoutProjectionsUrl}
                    alt={t('bitacora.cashflowWithoutProjections')}
                    className="bitacora-detail-image-preview"
                    onClick={() => window.open(bitacora.imageWithoutProjectionsUrl, '_blank')}
                  />
                </div>
              </div>
            ) : (
              <div className="bitacora-detail-image-empty">{t('bitacora.noImageWithoutProjections')}</div>
            )}
          </div>
        </div>
        {!imageWithProjections?.url && !bitacora?.imageWithProjectionsUrl && !imageWithoutProjections?.url && !bitacora?.imageWithoutProjectionsUrl && (
          <div className="bitacora-detail-images-empty">{t('bitacora.noImages')}</div>
        )}
      </div>

      <div className="bitacora-detail-section">
        <div className="bitacora-detail-section-title">{t('bitacora.initialBalances')}</div>
        <div className="bitacora-detail-banks">
          {initialBalancesForDisplay.length > 0 ? (
            initialBalancesForDisplay.map((bank, i) => (
              <div key={i} className="bitacora-detail-bank-row">
                <span>{bank.name}</span>
                <span>{formatMoneyWithCurrency(bank.amount, bank.currencyType)}</span>
                {bank.label && <span style={{ fontSize: '11px', color: '#6b7280' }}>{bank.label}</span>}
              </div>
            ))
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>{t('bitacora.noBalances')}</div>
          )}
        </div>
      </div>

      {canViewInitialComments && (
      <div ref={commentsSectionRef} className="bitacora-detail-section bitacora-detail-section--comments">
        <div className="bitacora-detail-section-header-row">
          <div className="bitacora-detail-section-title">{t('bitacora.internalAnalysis')}</div>
          {isEditable && ((canOpenChat && hasDraftContent) || (canGenerateDraftWithAi && !hasDraftContent)) && (
            <div className="bitacora-detail-toolbar-extra">
              {!chatOpen && canOpenChat && hasDraftContent && (
                <button
                  type="button"
                  className="bitacora-modal-ai-btn"
                  onClick={() => setChatOpen((v) => !v)}
                  title={t('bitacora.openAiChat')}
                >
                  <span className="bitacora-modal-ai-btn__content">
                    <img src={aiIcon} alt="IA" className="bitacora-modal-ai-btn__icon" />
                    <span>{t('bitacora.openAiChat')}</span>
                  </span>
                </button>
              )}
              {!hasDraftContent && canGenerateDraftWithAi && (
                <button
                  type="button"
                  className={`bitacora-modal-ai-btn${draftAiLoading ? ' is-loading' : ''}`}
                  onClick={handleGenerateDraftWithAi}
                  disabled={draftAiLoading}
                  title={draftAiLoading ? t('bitacora.generating') : t('bitacora.generateInitialAi')}
                >
                  <span className="bitacora-modal-ai-btn__content">
                    <img src={aiIcon} alt="IA" className={`bitacora-modal-ai-btn__icon${draftAiLoading ? ' is-loading' : ''}`} />
                    <span>{draftAiLoading ? t('bitacora.generating') : t('bitacora.generateDraftAi')}</span>
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
        {isEditable ? (
          <>
            {draftAiError && (
              <div className="bitacora-modal-ai-draft-error" style={{ marginBottom: 8 }}>{draftAiError}</div>
            )}
            {chatOpen && canOpenChat && (
              <div className="bitacora-detail-chat-wrap" style={{ marginBottom: 16 }}>
                <BitacoraChatPanel
                  bitacora={bitacora}
                  onClose={() => setChatOpen(false)}
                  onApplyReply={(reply) => {
                    setInitialComments(markdownToEditorHtml(reply));
                    dispatch(importAlertState({ content: t('bitacora.analysisUpdated'), state: 'success', duration: 3 }));
                    onRefresh?.();
                    setTimeout(() => {
                      commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  onAddAsComment={async (reply) => {
                    try {
                      const { error } = await createComment.executeMutation(bitacora.id, { comment: reply, source: 'ai' });
                      if (error) throw error;
                      dispatch(importAlertState({ content: t('bitacora.aiCommentAdded'), state: 'success', duration: 3 }));
                      onRefresh?.();
                    } catch (e) {
                      dispatch(importAlertState({ content: e?.message || t('bitacora.aiCommentError'), state: 'error', duration: 5 }));
                    }
                  }}
                />
              </div>
            )}
            <BitacoraEditor
              contenidoHtml={initialComments}
              onChange={setInitialComments}
              onDebouncedChange={handleDebouncedCommentsUpdate}
              placeholder={t('bitacora.analysisPlaceholder')}
              disabled={false}
              mentionUsers={mentionUsers}
            />
          </>
        ) : (
          <div className="bitacora-detail-comments-box">
            {hasInitialComments ? (
              <div
                className="bitacora-detail-comments bitacora-detail-comments--markdown"
                dangerouslySetInnerHTML={{ __html: renderBitacoraCommentWithMentions(commentsRaw) }}
              />
            ) : (
              <p className="bitacora-detail-comments-empty">{t('bitacora.noInitialComments')}</p>
            )}
          </div>
        )}
      </div>
      )}

      {(hasClientComment || !canViewInitialComments || isEditable) && (
        <div className="bitacora-detail-section bitacora-detail-section--comments">
          <div className="bitacora-detail-section-header-row">
            <div className="bitacora-detail-section-title">{t('bitacora.initialComment')}</div>
          </div>
          {isEditable ? (
            <BitacoraEditor
              contenidoHtml={emailDraft}
              onChange={setEmailDraft}
              onDebouncedChange={handleDebouncedEmailDraftUpdate}
              placeholder={t('bitacora.initialCommentPlaceholder')}
              disabled={false}
              mentionUsers={mentionUsers}
            />
          ) : (
            <div className="bitacora-detail-comments-box">
              {hasClientComment ? (
                <div
                  className="bitacora-detail-comments bitacora-detail-comments--markdown"
                  dangerouslySetInnerHTML={{ __html: renderBitacoraCommentWithMentions(clientCommentRaw) }}
                />
              ) : (
                <p className="bitacora-detail-comments-empty">{t('bitacora.noClientComment')}</p>
              )}
            </div>
          )}
        </div>
      )}

      {(canComment || isDraft) && (
        <div className="bitacora-detail-comments-thread">
          <BitacoraCommentsSection
            bitacora={bitacora}
            companyId={effectiveCompanyId}
            onCommentCreated={onRefresh}
          />
        </div>
      )}

      {canCreate && isDraft && (
        <div className="bitacora-detail-footer bitacora-detail-footer--delivery">
          <div className="bitacora-delivery-pending-left">
            <svg className="bitacora-delivery-clock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="bitacora-delivery-pending-text">{t('bitacora.pendingDelivery')}</span>
            <button
              type="button"
              className="bitacora-notify-btn"
              onClick={(e) => { e.stopPropagation(); onSend(bitacora); }}
              title={t('bitacora.notifyClientTitle')}
            >
              {t('bitacora.notify')}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="19" x2="19" y2="5" />
                <polyline points="7 5 19 5 19 17" />
              </svg>
            </button>
          </div>
          <div className="bitacora-detail-footer-actions">
            <button
              type="button"
              className="bitacora-history-btn"
              onClick={(e) => { e.stopPropagation(); onHistory && onHistory(bitacora); }}
              title={t('bitacora.viewHistory')}
            >
              {t('bitacora.viewHistory')}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="19" x2="19" y2="5" />
                <polyline points="7 5 19 5 19 17" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {embedded && isDelivered && (
        <div className="bitacora-detail-footer bitacora-detail-footer--delivery">
          <div className="bitacora-delivery-info-left">
            <svg className="bitacora-delivery-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="bitacora-delivery-text">
              Bitácora entregada el {formatDateTime(bitacora?.deliveredAt)} por{' '}
              <span className="bitacora-delivery-email">{bitacora?.deliveredByEmail || bitacora?.deliveredBy || '-'}</span>
              {canNotify && onNotify && (
                <>
                  {' '}
                  <span className="bitacora-delivery-sep">|</span>{' '}
                  <button
                    type="button"
                    className="bitacora-notify-btn bitacora-notify-btn--inline"
                    onClick={(e) => { e.stopPropagation(); onNotify(bitacora); }}
                  >
                    Notificar nuevamente
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="19" x2="19" y2="5" />
                      <polyline points="7 5 19 5 19 17" />
                    </svg>
                  </button>
                </>
              )}
            </span>
          </div>
          <div className="bitacora-detail-footer-actions">
            <button
              type="button"
              className="bitacora-history-btn"
              onClick={(e) => { e.stopPropagation(); onHistory && onHistory(bitacora); }}
              title={t('bitacora.viewHistory')}
            >
              {t('bitacora.viewHistory')}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="19" x2="19" y2="5" />
                <polyline points="7 5 19 5 19 17" />
              </svg>
            </button>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
});

BitacoraDetailView.displayName = 'BitacoraDetailView';

export default BitacoraDetailView;
